import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { toast } from 'sonner';
import { 
  ArrowUp, 
  ArrowDown, 
  MessageSquare, 
  Share2, 
  MoreHorizontal,
  Edit3,
  Search,
  TrendingUp,
  Users,
  Bookmark,
  Bell,
  User
} from 'lucide-react';
import { getAuthHeaders, getUser } from '../lib/auth';
import Navigation from '../components/Navigation';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CommunityNew = () => {
  const navigate = useNavigate();
  const currentUser = getUser();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewPost, setShowNewPost] = useState(false);
  const [postText, setPostText] = useState('');
  const [posting, setPosting] = useState(false);
  const [commentText, setCommentText] = useState({});
  const [showComments, setShowComments] = useState({});
  const [postComments, setPostComments] = useState({});
  const [upvotes, setUpvotes] = useState({});

  useEffect(() => {
    loadPosts();
  }, []);

  const loadPosts = async () => {
    try {
      const response = await axios.get(`${API}/community/list`, {
        headers: getAuthHeaders()
      });
      setPosts(response.data.posts);
      
      // Initialize upvotes (simulate - would come from backend in production)
      const initialUpvotes = {};
      response.data.posts.forEach(post => {
        initialUpvotes[post.id] = Math.floor(Math.random() * 50);
      });
      setUpvotes(initialUpvotes);
    } catch (error) {
      toast.error('Failed to load posts');
    } finally {
      setLoading(false);
    }
  };

  const loadComments = async (postId) => {
    try {
      const response = await axios.get(`${API}/community/post/${postId}/comments`, {
        headers: getAuthHeaders()
      });
      setPostComments(prev => ({ ...prev, [postId]: response.data.comments }));
    } catch (error) {
      console.error('Failed to load comments');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!postText.trim()) return;

    setPosting(true);
    try {
      await axios.post(`${API}/community/create`, { text: postText }, {
        headers: getAuthHeaders()
      });
      
      setPostText('');
      setShowNewPost(false);
      toast.success('Question posted!');
      await loadPosts();
    } catch (error) {
      toast.error('Failed to create post');
    } finally {
      setPosting(false);
    }
  };

  const handleComment = async (postId) => {
    const text = commentText[postId];
    if (!text?.trim()) return;

    try {
      await axios.post(`${API}/community/comment`, {
        postId,
        text
      }, { headers: getAuthHeaders() });
      
      setCommentText(prev => ({ ...prev, [postId]: '' }));
      toast.success('Answer posted!');
      await loadComments(postId);
    } catch (error) {
      toast.error('Failed to post answer');
    }
  };

  const toggleComments = (postId) => {
    const newShowComments = !showComments[postId];
    setShowComments(prev => ({ ...prev, [postId]: newShowComments }));
    
    if (newShowComments && !postComments[postId]) {
      loadComments(postId);
    }
  };

  const handleUpvote = (postId) => {
    setUpvotes(prev => ({ ...prev, [postId]: (prev[postId] || 0) + 1 }));
    toast.success('Upvoted!');
  };

  const handleDownvote = (postId) => {
    setUpvotes(prev => ({ ...prev, [postId]: Math.max(0, (prev[postId] || 0) - 1) }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navigation />
        <div className="flex items-center justify-center py-20">
          <p className="text-slate-600">Loading community...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-12 gap-6">
          {/* Main Content */}
          <div className="col-span-12 lg:col-span-8">
            {/* Ask Question Box */}
            <Card className="mb-4 bg-white border-slate-200">
              <div className="p-4">
                <div className="flex items-center gap-3">
                  <Avatar className="w-10 h-10">
                    <AvatarImage src={currentUser?.profilePicUrl} />
                    <AvatarFallback className="bg-slate-200">
                      <User className="w-5 h-5 text-slate-500" />
                    </AvatarFallback>
                  </Avatar>
                  <button
                    onClick={() => setShowNewPost(true)}
                    className="flex-1 text-left px-4 py-3 rounded-full bg-slate-50 hover:bg-slate-100 text-slate-600 transition-colors border border-slate-200"
                  >
                    What do you want to ask or share?
                  </button>
                </div>
              </div>
            </Card>

            {/* New Post Modal/Form */}
            {showNewPost && (
              <Card className="mb-4 bg-white border-slate-200">
                <div className="p-6">
                  <div className="flex items-start gap-3 mb-4">
                    <Avatar className="w-10 h-10">
                      <AvatarImage src={currentUser?.profilePicUrl} />
                      <AvatarFallback className="bg-slate-200">
                        <User className="w-5 h-5 text-slate-500" />
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <p className="font-medium text-slate-900">{currentUser?.name}</p>
                    </div>
                  </div>
                  
                  <Textarea
                    value={postText}
                    onChange={(e) => setPostText(e.target.value)}
                    placeholder="Start your question with 'What', 'How', 'Why', etc."
                    className="min-h-[120px] mb-4 border-slate-300 focus:border-blue-500 resize-none"
                    autoFocus
                  />
                  
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-slate-500">
                      {postText.length} characters
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        onClick={() => {
                          setShowNewPost(false);
                          setPostText('');
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleSubmit}
                        disabled={posting || !postText.trim()}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        {posting ? 'Posting...' : 'Post'}
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            )}

            {/* Posts Feed */}
            <div className="space-y-3">
              {posts.length === 0 ? (
                <Card className="p-12 text-center bg-white border-slate-200">
                  <MessageSquare className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">No posts yet</h3>
                  <p className="text-slate-600 mb-4">Be the first to ask a question!</p>
                  <Button
                    onClick={() => setShowNewPost(true)}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    Ask Question
                  </Button>
                </Card>
              ) : (
                posts.map((post) => (
                  <Card key={post.id} className="bg-white border-slate-200 hover:border-slate-300 transition-colors">
                    <div className="p-4">
                      {/* Post Header */}
                      <div className="flex items-start gap-3 mb-3">
                        <Avatar 
                          className="w-10 h-10 cursor-pointer"
                          onClick={() => post.author?.id && navigate(`/profile/${post.author.id}`)}
                        >
                          <AvatarImage src={post.author?.profilePicUrl} />
                          <AvatarFallback className="bg-slate-200">
                            <User className="w-5 h-5 text-slate-500" />
                          </AvatarFallback>
                        </Avatar>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => post.author?.id && navigate(`/profile/${post.author.id}`)}
                              className="font-semibold text-slate-900 hover:underline"
                            >
                              {post.author?.name || 'User'}
                            </button>
                            <span className="text-slate-500">·</span>
                            <span className="text-sm text-slate-500">
                              {dayjs(post.createdAt).fromNow()}
                            </span>
                          </div>
                          {post.author?.businessCategory && (
                            <p className="text-sm text-slate-600">{post.author.businessCategory}</p>
                          )}
                        </div>

                        <Button variant="ghost" size="sm" className="text-slate-400">
                          <MoreHorizontal className="w-5 h-5" />
                        </Button>
                      </div>

                      {/* Post Content */}
                      <div className="mb-4">
                        <p className="text-lg text-slate-900 leading-relaxed whitespace-pre-wrap">
                          {post.text}
                        </p>
                      </div>

                      {/* Action Bar */}
                      <div className="flex items-center gap-1 pt-3 border-t border-slate-100">
                        {/* Upvote */}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleUpvote(post.id)}
                          className="flex items-center gap-2 hover:bg-slate-100"
                        >
                          <ArrowUp className="w-5 h-5" />
                          <span className="font-medium">{upvotes[post.id] || 0}</span>
                        </Button>

                        {/* Downvote */}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDownvote(post.id)}
                          className="hover:bg-slate-100"
                        >
                          <ArrowDown className="w-5 h-5" />
                        </Button>

                        {/* Answer/Comment */}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleComments(post.id)}
                          className="flex items-center gap-2 hover:bg-slate-100 ml-2"
                        >
                          <MessageSquare className="w-5 h-5" />
                          <span>
                            {postComments[post.id]?.length || 0} Answer{postComments[post.id]?.length !== 1 ? 's' : ''}
                          </span>
                        </Button>

                        {/* Share */}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="flex items-center gap-2 hover:bg-slate-100 ml-auto"
                        >
                          <Share2 className="w-5 h-5" />
                          <span>Share</span>
                        </Button>
                      </div>

                      {/* Comments Section */}
                      {showComments[post.id] && (
                        <div className="mt-4 pt-4 border-t border-slate-200">
                          {/* Add Answer */}
                          <div className="mb-4">
                            <div className="flex items-start gap-3">
                              <Avatar className="w-8 h-8">
                                <AvatarImage src={currentUser?.profilePicUrl} />
                                <AvatarFallback className="bg-slate-200">
                                  <User className="w-4 h-4 text-slate-500" />
                                </AvatarFallback>
                              </Avatar>
                              <div className="flex-1">
                                <Textarea
                                  value={commentText[post.id] || ''}
                                  onChange={(e) => setCommentText(prev => ({ ...prev, [post.id]: e.target.value }))}
                                  placeholder="Write your answer..."
                                  className="min-h-[80px] mb-2 border-slate-300 focus:border-blue-500 resize-none text-sm"
                                />
                                <Button
                                  onClick={() => handleComment(post.id)}
                                  disabled={!commentText[post.id]?.trim()}
                                  size="sm"
                                  className="bg-blue-600 hover:bg-blue-700"
                                >
                                  Add Answer
                                </Button>
                              </div>
                            </div>
                          </div>

                          {/* Comments List */}
                          <div className="space-y-4">
                            {postComments[post.id]?.map((comment) => (
                              <div key={comment.id} className="flex items-start gap-3">
                                <Avatar className="w-8 h-8">
                                  <AvatarImage src={comment.author?.profilePicUrl} />
                                  <AvatarFallback className="bg-slate-200">
                                    <User className="w-4 h-4 text-slate-500" />
                                  </AvatarFallback>
                                </Avatar>
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="font-semibold text-sm text-slate-900">
                                      {comment.author?.name || 'User'}
                                    </span>
                                    <span className="text-xs text-slate-500">
                                      {dayjs(comment.createdAt).fromNow()}
                                    </span>
                                  </div>
                                  <p className="text-sm text-slate-700 leading-relaxed">
                                    {comment.text}
                                  </p>
                                  <div className="flex items-center gap-3 mt-2">
                                    <button className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1">
                                      <ArrowUp className="w-3 h-3" />
                                      Upvote
                                    </button>
                                    <button className="text-xs text-slate-500 hover:text-slate-700">
                                      Reply
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </Card>
                ))
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="hidden lg:block lg:col-span-4">
            <div className="sticky top-20 space-y-4">
              {/* Spaces to Follow */}
              <Card className="bg-white border-slate-200">
                <div className="p-4">
                  <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <Users className="w-5 h-5 text-slate-600" />
                    Spaces to follow
                  </h3>
                  <div className="space-y-3">
                    {[
                      { name: 'Business & Finance', members: '2.5M' },
                      { name: 'Technology', members: '5.2M' },
                      { name: 'Healthcare', members: '1.8M' },
                      { name: 'Marketing & Sales', members: '3.1M' }
                    ].map((space, idx) => (
                      <div key={idx} className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-sm text-slate-900">{space.name}</p>
                          <p className="text-xs text-slate-500">{space.members} members</p>
                        </div>
                        <Button size="sm" variant="outline" className="text-xs">
                          Follow
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>

              {/* Trending Questions */}
              <Card className="bg-white border-slate-200">
                <div className="p-4">
                  <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-slate-600" />
                    Trending
                  </h3>
                  <div className="space-y-3">
                    {[
                      'What are the best practices for remote work?',
                      'How to start a small business in 2025?',
                      'What is the future of AI in healthcare?'
                    ].map((question, idx) => (
                      <button
                        key={idx}
                        className="text-left w-full text-sm text-slate-700 hover:text-blue-600 transition-colors"
                      >
                        {question}
                      </button>
                    ))}
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommunityNew;
